import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Heart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ValuesQuickViewModal } from './ValuesQuickViewModal';

interface CoreValue {
  id: string;
  value_name: string;
  description: string | null;
  importance_rank: number | null;
}

export const ValuesBadge = () => {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: values = [] } = useQuery<CoreValue[]>({
    queryKey: ['core-values-badge', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('core_values')
        .select('id, value_name, description, importance_rank')
        .eq('user_id', user.id)
        .order('importance_rank', { ascending: true });
      if (error) return [];
      return data ?? [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const topValues = values.slice(0, 2);

  if (topValues.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
        title="View your core values"
      >
        <Heart className="w-3.5 h-3.5 text-primary" />
        <div className="flex items-center gap-1">
          {topValues.map((v) => (
            <Badge
              key={v.id}
              variant="secondary"
              className="text-xs px-2 py-0 h-5 font-medium"
            >
              {v.value_name}
            </Badge>
          ))}
          {values.length > 2 && (
            <span className="text-xs text-muted-foreground">
              +{values.length - 2}
            </span>
          )}
        </div>
      </button>
      <ValuesQuickViewModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        values={values}
      />
    </>
  );
};
